// src/lib/fetch-client.js
// GraphQL client with explicit Next.js Data Cache (force-cache + tags + revalidate).

// Request deduplication within a single render / request
const pendingRequests = new Map();

export async function fetchClient(query, variables, token, livePreview, tags = []) {
  const requestKey = JSON.stringify({ query, variables, token, livePreview });

  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }

  const path =
    token && livePreview
      ? `/graphql?token=${token}&live-preview=${livePreview}`
      : "/graphql";

  let normalizedVariables = {};
  if (variables && typeof variables === "object") {
    normalizedVariables = Object.fromEntries(
      Object.entries(variables).filter(([, value]) => value !== undefined)
    );
  }

  const body = JSON.stringify({
    query,
    variables: normalizedVariables,
  });

  const statamicHost = process.env.NEXT_PUBLIC_STATAMIC_URL?.trim();
  if (!statamicHost) {
    console.warn(
      "NEXT_PUBLIC_STATAMIC_URL is not set — GraphQL skipped (set it in Vercel/env for builds and runtime)."
    );
    return { data: null };
  }

  const endpoint = "https://" + statamicHost + path;

  const makeRequest = async () => {
    // Preview / draft-mode, and local dev, must never be served from the Data Cache
    const isPreview = Boolean(token && livePreview);
    const bypassCache = isPreview || process.env.NODE_ENV === "development";

    const result = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
      cache: bypassCache ? "no-store" : "force-cache",
      next: bypassCache
        ? undefined
        : {
            tags,
            revalidate: 86400,
          },
    });

    if (!result.ok) {
      throw new Error(
        `GraphQL request failed with status ${result.status}: ${result.statusText}`
      );
    }

    const data = await result.json();

    if (data.errors?.length) {
      const errorMessage = data.errors.map((err) => err.message).join(", ");
      console.warn("⚠️ GraphQL errors detected:", {
        errorCount: data.errors.length,
        errors: data.errors,
        endpoint,
        hasData: !!data.data,
        tags,
      });

      if (!data.data) {
        throw new Error(`GraphQL errors with no data: ${errorMessage}`);
      }
    }

    return data;
  };

  const requestPromise = makeRequest();
  pendingRequests.set(requestKey, requestPromise);

  try {
    return await requestPromise;
  } finally {
    pendingRequests.delete(requestKey);
  }
}
